import { JSNS, type JsonObject } from '@zimbra-webif/types';
import type {
  AddressList,
  AddressListMembersResult,
  CalendarResource,
  CalendarResourceSearchResult,
  ContactInfo,
  Cursor,
  DistributionList,
  DistributionListActionOp,
  DistributionListActionPayload,
  DistributionListDetail,
  DistributionListGranteeInfo,
  DistributionListGranteeSelector,
  DistributionListMembersResult,
  DistributionListRightInfo,
  DistributionListRightSpec,
  DistributionListSelector,
  DistributionListSubscribeStatus,
  EntrySearchFilter,
  EntrySearchFilterCond,
  EntrySearchFilterMultiCond,
  GalContact,
  GalSearchResult,
  GalSearchType,
  GalSyncResult,
  HabGroupMember,
  KeyValuePair,
  MemberOfSelector,
} from '../../../types/src/contacts-entities';
import type { ZimbraClient } from '../client';

/** Normalize an absent/single/array Zimbra field to an array. */
function asArray<T>(v: T | T[] | undefined): T[] {
  if (v === undefined) return [];
  return Array.isArray(v) ? v : [v];
}

/** Build the `<cursor>` element from a {@link Cursor}. */
function buildCursor(c: Cursor): JsonObject {
  const cursor: JsonObject = {};
  if (c.id !== undefined) cursor.id = c.id;
  if (c.sortVal !== undefined) cursor.sortVal = c.sortVal;
  if (c.endSortVal !== undefined) cursor.endSortVal = c.endSortVal;
  if (c.includeOffset !== undefined) cursor.includeOffset = c.includeOffset ? 1 : 0;
  return cursor;
}

/** Build the `<searchFilter>` (EntrySearchFilterInfo) element. */
function buildSearchFilter(f: EntrySearchFilter): JsonObject {
  const out: JsonObject = {};
  if (f.cond) out.cond = buildCond(f.cond);
  if (f.conds) out.conds = buildMultiCond(f.conds);
  return out;
}

function buildCond(c: EntrySearchFilterCond): JsonObject {
  const out: JsonObject = { attr: c.attr, op: c.op, value: c.value };
  if (c.not !== undefined) out.not = c.not ? 1 : 0;
  return out;
}

function buildMultiCond(m: EntrySearchFilterMultiCond): JsonObject {
  const out: JsonObject = {};
  if (m.not !== undefined) out.not = m.not ? 1 : 0;
  if (m.or !== undefined) out.or = m.or ? 1 : 0;
  if (m.cond?.length) out.cond = m.cond.map(buildCond);
  if (m.conds?.length) out.conds = m.conds.map(buildMultiCond);
  return out;
}

interface GalSearchResponse {
  cn?: ContactInfo | ContactInfo[];
  sortBy?: string;
  offset?: number;
  more?: boolean;
  paginationSupported?: boolean;
  tokenizeKey?: boolean;
  [k: string]: unknown;
}

interface AutoCompleteGalResponse {
  cn?: ContactInfo | ContactInfo[];
  more?: boolean;
  tokenizeKey?: boolean;
  paginationSupported?: boolean;
  [k: string]: unknown;
}

interface SyncGalResponse {
  cn?: ContactInfo | ContactInfo[];
  deleted?: { id: string } | Array<{ id: string }>;
  token?: string;
  more?: boolean;
  galDefinitionLastModified?: string;
  throttled?: boolean;
  fullSyncRecommended?: boolean;
  remain?: number;
  [k: string]: unknown;
}

interface DLInfoEnvelope {
  dl?: DistributionList | DistributionList[];
  [k: string]: unknown;
}

interface GetDLResponse {
  dl?: GetDLDetailWire | GetDLDetailWire[];
  [k: string]: unknown;
}

interface GetDLDetailWire extends DistributionList {
  dlm?: string | string[];
  owners?: { owner?: DistributionListGranteeInfo | DistributionListGranteeInfo[] };
  rights?: { right?: DistributionListRightInfo | DistributionListRightInfo[] };
}

interface GetDLMembersResponse {
  dlm?: string | string[];
  groupMembers?: { groupMember?: HabGroupMember | HabGroupMember[] };
  more?: boolean;
  total?: number;
  [k: string]: unknown;
}

interface SubscribeDLResponse {
  status?: DistributionListSubscribeStatus;
  [k: string]: unknown;
}

interface AddressListsResponse {
  addressLists?: { addressList?: AddressList | AddressList[] };
  [k: string]: unknown;
}

interface AddressListMembersResponse {
  alm?: string | string[];
  more?: boolean;
  total?: number;
  [k: string]: unknown;
}

interface CalResourceSearchResponse {
  calresource?: CalendarResource | CalendarResource[];
  sortBy?: string;
  offset?: number;
  more?: boolean;
  paginationSupported?: boolean;
  [k: string]: unknown;
}

/** Build a `<dl>` DistributionListSelector wire element from `{ by, key }`. */
function buildDlSelector(dl: DistributionListSelector): JsonObject {
  return { by: dl.by, _content: dl.key };
}

/** Distribution-list operations (account-ns). */
class DistributionListApi {
  constructor(private readonly client: ZimbraClient) {}

  /**
   * GetAccountDistributionListsRequest — DLs the account owns and/or is a
   * member of.
   */
  async listForAccount(opts: {
    ownerOf?: boolean;
    memberOf?: MemberOfSelector;
    attrs?: string[];
  } = {}): Promise<DistributionList[]> {
    const params: JsonObject = {};
    if (opts.ownerOf !== undefined) params.ownerOf = opts.ownerOf ? 1 : 0;
    if (opts.memberOf !== undefined) params.memberOf = opts.memberOf;
    if (opts.attrs?.length) params.attrs = opts.attrs.join(',');
    const res = await this.client.call<DLInfoEnvelope>(
      JSNS.account,
      'GetAccountDistributionLists',
      params,
    );
    return asArray(res.dl);
  }

  /** GetDistributionListRequest — full detail for one DL. */
  async get(
    by: 'id' | 'name',
    key: string,
    opts: { needOwners?: boolean; needRights?: string[]; attrs?: string[] } = {},
  ): Promise<DistributionListDetail> {
    const params: JsonObject = { dl: buildDlSelector({ by, key }) };
    if (opts.needOwners !== undefined) params.needOwners = opts.needOwners ? 1 : 0;
    if (opts.needRights?.length) params.needRights = opts.needRights.join(',');
    if (opts.attrs?.length) params.a = opts.attrs.map((n) => ({ n }));
    const res = await this.client.call<GetDLResponse>(JSNS.account, 'GetDistributionList', params);
    const wire = asArray(res.dl)[0] ?? {};
    return this.toDetail(wire);
  }

  /** GetDistributionListMembersRequest — paged member list of a DL. */
  async members(
    dl: string,
    opts: { limit?: number; offset?: number } = {},
  ): Promise<DistributionListMembersResult> {
    const params: JsonObject = { dl: { _content: dl } };
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    const res = await this.client.call<GetDLMembersResponse>(
      JSNS.account,
      'GetDistributionListMembers',
      params,
    );
    const hab = asArray(res.groupMembers?.groupMember);
    const result: DistributionListMembersResult = {
      members: asArray(res.dlm),
      total: res.total ?? 0,
      more: res.more ?? false,
    };
    if (hab.length > 0) result.habGroupMembers = hab;
    return result;
  }

  /**
   * DistributionListActionRequest — owner/admin op on a DL (rename, delete,
   * modify attrs, add/remove/set members & owners, grant/revoke/set rights,
   * accept/reject subscription requests).
   */
  async action(
    dl: DistributionListSelector,
    op: DistributionListActionOp,
    payload: DistributionListActionPayload = {},
  ): Promise<void> {
    const action: JsonObject = { op };
    if (payload.members?.length) action.dlm = payload.members.map((m) => ({ _content: m }));
    if (payload.newName !== undefined) action.newName = { _content: payload.newName };
    if (payload.owners?.length) action.owner = payload.owners.map(buildGranteeSelector);
    if (payload.rights?.length) action.right = payload.rights.map(buildRightSpec);
    if (payload.subsReq !== undefined) action.subsReq = { ...payload.subsReq };
    const params: JsonObject = { dl: buildDlSelector(dl), action };
    if (payload.attrs) {
      params.a = Object.entries(payload.attrs).map(([n, v]) => ({ n, _content: v }));
    }
    await this.client.call(JSNS.account, 'DistributionListAction', params);
  }

  /** Sugar: add members to a DL. */
  async addMembers(dl: DistributionListSelector, members: string[]): Promise<void> {
    return this.action(dl, 'addMembers', { members });
  }

  /** Sugar: remove members from a DL. */
  async removeMembers(dl: DistributionListSelector, members: string[]): Promise<void> {
    return this.action(dl, 'removeMembers', { members });
  }

  /** Sugar: rename a DL. */
  async rename(dl: DistributionListSelector, newName: string): Promise<void> {
    return this.action(dl, 'rename', { newName });
  }

  /** Sugar: grant rights on a DL. */
  async grantRights(dl: DistributionListSelector, rights: DistributionListRightSpec[]): Promise<void> {
    return this.action(dl, 'grantRights', { rights });
  }

  /** Sugar: delete a DL. */
  async delete(dl: DistributionListSelector): Promise<void> {
    return this.action(dl, 'delete');
  }

  /** SubscribeDistributionListRequest — self-service join a DL. */
  async subscribe(dl: DistributionListSelector): Promise<DistributionListSubscribeStatus> {
    return this.doSubscribe(dl, 'subscribe');
  }

  /** SubscribeDistributionListRequest — self-service leave a DL. */
  async unsubscribe(dl: DistributionListSelector): Promise<DistributionListSubscribeStatus> {
    return this.doSubscribe(dl, 'unsubscribe');
  }

  /** CreateDistributionListRequest — create a new (static or dynamic) DL. */
  async create(
    name: string,
    opts: { dynamic?: boolean; attrs?: Record<string, string> } = {},
  ): Promise<DistributionList> {
    const params: JsonObject = { name };
    if (opts.dynamic !== undefined) params.dynamic = opts.dynamic ? 1 : 0;
    if (opts.attrs) {
      params.a = Object.entries(opts.attrs).map(([n, v]) => ({ n, _content: v }));
    }
    const res = await this.client.call<DLInfoEnvelope>(
      JSNS.account,
      'CreateDistributionList',
      params,
    );
    return asArray(res.dl)[0] ?? {};
  }

  private async doSubscribe(
    dl: DistributionListSelector,
    op: 'subscribe' | 'unsubscribe',
  ): Promise<DistributionListSubscribeStatus> {
    const res = await this.client.call<SubscribeDLResponse>(
      JSNS.account,
      'SubscribeDistributionList',
      { op, dl: buildDlSelector(dl) },
    );
    return res.status ?? 'awaiting_approval';
  }

  private toDetail(wire: GetDLDetailWire): DistributionListDetail {
    const { dlm, owners, rights, ...rest } = wire;
    const detail: DistributionListDetail = { ...rest };
    const members = asArray(dlm);
    if (members.length > 0) detail.members = members;
    const ownerList = asArray(owners?.owner);
    if (ownerList.length > 0) detail.owners = ownerList;
    const rightList = asArray(rights?.right);
    if (rightList.length > 0) detail.rights = rightList;
    return detail;
  }
}

function buildGranteeSelector(g: DistributionListGranteeSelector): JsonObject {
  const out: JsonObject = {};
  if (g.type !== undefined) out.type = g.type;
  if (g.by !== undefined) out.by = g.by;
  if (g._content !== undefined) out._content = g._content;
  return out;
}

function buildRightSpec(r: DistributionListRightSpec): JsonObject {
  const out: JsonObject = { right: r.right };
  if (r.grantee?.length) out.grantee = r.grantee.map(buildGranteeSelector);
  return out;
}

/** Address-list operations (account-ns). */
class AddressListApi {
  constructor(private readonly client: ZimbraClient) {}

  /** GetAllAddressListsRequest — all address lists available to the account. */
  async list(): Promise<AddressList[]> {
    const res = await this.client.call<AddressListsResponse>(
      JSNS.account,
      'GetAllAddressLists',
      {},
    );
    return asArray(res.addressLists?.addressList);
  }

  /** GetAddressListMembersRequest — paged member list (or count) of a list. */
  async members(
    id: string,
    opts: { limit?: number; offset?: number; countOnly?: boolean } = {},
  ): Promise<AddressListMembersResult> {
    const params: JsonObject = { id };
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    if (opts.countOnly !== undefined) params.countOnly = opts.countOnly ? 1 : 0;
    const res = await this.client.call<AddressListMembersResponse>(
      JSNS.account,
      'GetAddressListMembers',
      params,
    );
    return {
      members: asArray(res.alm),
      total: res.total ?? 0,
      more: res.more ?? false,
    };
  }
}

/** Calendar-resource search (account-ns). */
class CalendarResourceApi {
  constructor(private readonly client: ZimbraClient) {}

  /** SearchCalendarResourcesRequest — search bookable rooms/equipment. */
  async search(opts: {
    name?: string;
    searchFilter?: EntrySearchFilter;
    sortBy?: string;
    limit?: number;
    offset?: number;
    cursor?: Cursor;
    attrs?: string[];
    quick?: boolean;
    galAccountId?: string;
    locale?: string;
  } = {}): Promise<CalendarResourceSearchResult> {
    const params: JsonObject = {};
    if (opts.quick !== undefined) params.quick = opts.quick ? 1 : 0;
    if (opts.sortBy !== undefined) params.sortBy = opts.sortBy;
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    if (opts.galAccountId !== undefined) params.galAcctId = opts.galAccountId;
    if (opts.attrs?.length) params.attrs = opts.attrs.join(',');
    if (opts.locale !== undefined) params.locale = { _content: opts.locale };
    if (opts.cursor !== undefined) params.cursor = buildCursor(opts.cursor);
    if (opts.name !== undefined) params.name = { _content: opts.name };
    if (opts.searchFilter !== undefined) params.searchFilter = buildSearchFilter(opts.searchFilter);
    const res = await this.client.call<CalResourceSearchResponse>(
      JSNS.account,
      'SearchCalendarResources',
      params,
    );
    const out: CalendarResourceSearchResult = {
      resources: asArray(res.calresource),
      more: res.more ?? false,
      offset: res.offset ?? 0,
    };
    if (res.sortBy !== undefined) out.sortBy = res.sortBy;
    if (res.paginationSupported !== undefined) out.paginationSupported = res.paginationSupported;
    return out;
  }
}

/**
 * GAL / directory domain (urn:zimbraAccount) — Global Address List search,
 * autocomplete and sync, plus the directory objects that hang off it:
 * distribution lists, address lists, and calendar resources.
 *
 * Sub-surfaces: `gal.dl`, `gal.addressLists`, `gal.calendarResources`.
 * One public method per SOAP command, typed in and out.
 */
export class GalApi {
  /** Distribution-list operations. */
  readonly dl: DistributionListApi;
  /** Address-list operations. */
  readonly addressLists: AddressListApi;
  /** Calendar-resource search. */
  readonly calendarResources: CalendarResourceApi;

  constructor(private readonly client: ZimbraClient) {
    this.dl = new DistributionListApi(client);
    this.addressLists = new AddressListApi(client);
    this.calendarResources = new CalendarResourceApi(client);
  }

  /** SearchGalRequest — search the Global Address List with paging/filter. */
  async search(opts: {
    name?: string;
    query?: string;
    ref?: string;
    type?: GalSearchType;
    limit?: number;
    offset?: number;
    cursor?: Cursor;
    sortBy?: string;
    needCanExpand?: boolean;
    needIsOwner?: boolean;
    needIsMember?: MemberOfSelector;
    needSMIMECerts?: boolean;
    searchFilter?: EntrySearchFilter;
    galAccountId?: string;
    quick?: boolean;
    locale?: string;
  } = {}): Promise<GalSearchResult> {
    const params: JsonObject = {};
    if (opts.ref !== undefined) params.ref = opts.ref;
    if (opts.name !== undefined) params.name = opts.name;
    if (opts.type !== undefined) params.type = opts.type;
    if (opts.needCanExpand !== undefined) params.needExp = opts.needCanExpand ? 1 : 0;
    if (opts.needIsOwner !== undefined) params.needIsOwner = opts.needIsOwner ? 1 : 0;
    if (opts.needIsMember !== undefined) params.needIsMember = opts.needIsMember;
    if (opts.needSMIMECerts !== undefined) params.needSMIMECerts = opts.needSMIMECerts ? 1 : 0;
    if (opts.galAccountId !== undefined) params.galAcctId = opts.galAccountId;
    if (opts.quick !== undefined) params.quick = opts.quick ? 1 : 0;
    if (opts.sortBy !== undefined) params.sortBy = opts.sortBy;
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.offset !== undefined) params.offset = opts.offset;
    if (opts.locale !== undefined) params.locale = { _content: opts.locale };
    if (opts.cursor !== undefined) params.cursor = buildCursor(opts.cursor);
    if (opts.query !== undefined) params.query = { _content: opts.query };
    if (opts.searchFilter !== undefined) params.searchFilter = buildSearchFilter(opts.searchFilter);
    const res = await this.client.call<GalSearchResponse>(JSNS.account, 'SearchGal', params);
    const out: GalSearchResult = {
      entries: asArray<GalContact>(res.cn),
      more: res.more ?? false,
      offset: res.offset ?? 0,
      paginationSupported: res.paginationSupported ?? false,
    };
    if (res.sortBy !== undefined) out.sortBy = res.sortBy;
    if (res.tokenizeKey !== undefined) out.tokenizeKey = res.tokenizeKey;
    return out;
  }

  /** AutoCompleteGalRequest — type-ahead GAL lookup for the address picker. */
  async autocomplete(
    name: string,
    opts: {
      type?: GalSearchType;
      limit?: number;
      needCanExpand?: boolean;
      galAccountId?: string;
    } = {},
  ): Promise<GalContact[]> {
    const params: JsonObject = { name };
    if (opts.type !== undefined) params.type = opts.type;
    if (opts.limit !== undefined) params.limit = opts.limit;
    if (opts.needCanExpand !== undefined) params.needExp = opts.needCanExpand ? 1 : 0;
    if (opts.galAccountId !== undefined) params.galAcctId = opts.galAccountId;
    const res = await this.client.call<AutoCompleteGalResponse>(
      JSNS.account,
      'AutoCompleteGal',
      params,
    );
    return asArray<GalContact>(res.cn);
  }

  /** SyncGalRequest — incremental token-based GAL sync (changed `cn` + deleted ids). */
  async sync(opts: {
    token?: string;
    idOnly?: boolean;
    limit?: number;
    getCount?: boolean;
    galAccountId?: string;
  } = {}): Promise<GalSyncResult> {
    const params: JsonObject = {};
    if (opts.token !== undefined) params.token = opts.token;
    if (opts.galAccountId !== undefined) params.galAcctId = opts.galAccountId;
    if (opts.idOnly !== undefined) params.idOnly = opts.idOnly ? 1 : 0;
    if (opts.getCount !== undefined) params.getCount = opts.getCount ? 1 : 0;
    if (opts.limit !== undefined) params.limit = opts.limit;
    const res = await this.client.call<SyncGalResponse>(JSNS.account, 'SyncGal', params);
    const out: GalSyncResult = {
      token: res.token ?? '',
      more: res.more ?? false,
      fullSyncRecommended: res.fullSyncRecommended ?? false,
      throttled: res.throttled ?? false,
      lastModified: res.galDefinitionLastModified ?? '',
      changed: asArray<GalContact>(res.cn),
      deleted: asArray(res.deleted).map((d) => d.id),
    };
    if (res.remain !== undefined) out.remain = res.remain;
    return out;
  }
}

/** Re-exported helper types so callers can build typed payloads. */
export type { KeyValuePair };
